import { createContext, useContext, ReactNode } from 'react';
import { useFamilyMembers } from '@/hooks/useFamilyMembers';
import { usePresence } from '@/hooks/usePresence';
import { FamilyMember } from '@/types/familyMember';
import { useTeam } from '@/contexts/TeamContext';
import { useAuth } from '@/hooks/useAuth';

interface FamilyContextType {
  members: FamilyMember[];
  currentUser: FamilyMember | null;
  currentUserId: string | null;
  setCurrentUserId: (id: string | null) => void;
  onlineMemberIds: string[];
  addMember: (name: string) => Promise<string | null>;
  updateMember: (id: string, updates: { name?: string; email?: string | null }) => Promise<void>;
  deleteMember: (id: string) => Promise<void>;
  reorderMembers: (reorderedMembers: FamilyMember[]) => Promise<void>;
  linkMemberToUser: (memberId: string, userId: string) => Promise<boolean>;
  findMemberByEmail: (email: string) => FamilyMember | undefined;
  findMemberByUserId: (userId: string) => FamilyMember | undefined;
  getDisplayName: (targetMemberId: string, viewerMemberId?: string) => string;
  getMemberAvatarUrl: (memberId: string) => string | null;
  loading: boolean;
}

const FamilyContext = createContext<FamilyContextType | undefined>(undefined);

export function FamilyProvider({ children }: { children: ReactNode }) {
  const { currentTeamId } = useTeam();
  const { user, loading: authLoading } = useAuth();

  // Pass user info only when auth is done loading - prevents stale values
  const authUserId = authLoading ? undefined : (user?.id ?? null);
  const authUserEmail = authLoading ? undefined : (user?.email ?? null);
  const familyData = useFamilyMembers(currentTeamId, authUserId, authUserEmail);
  const { onlineMemberIds } = usePresence(currentTeamId, familyData.currentUserId);

  return (
    <FamilyContext.Provider value={{ ...familyData, onlineMemberIds }}>
      {children}
    </FamilyContext.Provider>
  );
}

export function useFamily() {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return context;
}
